// src/pages/Checkout.jsx
import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react'; // Added useContext and useCallback
import { useCart } from '../context/CartContext'; // Use updated context
import { useApp } from '../context/AppContext';
import { cn } from "@/lib/utils";
import { useAuth } from '../context/AuthContext'; // Import useAuth
// import { useUsagePeriod } from '../context/UsagePeriodContext';
import { useRentmanCustomers } from '../hooks/useRentmanCustomers';
import { useRentmanContactPersons } from '../hooks/useRentmanContactPersons';
import { getFunctionsBaseUrl } from '../lib/functionsBaseUrl';

// Shadcn UI Imports
import { Button, buttonVariants } from "@/components/ui/button"; // 
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner"; // Use sonner for notifications
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose // Added DialogClose if needed later
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog"; // For Clear Cart Confirmation


// Icons & Custom Components
import { MoreHorizontal, Loader2, Trash2, ChevronDown } from "lucide-react";
import CartCounter from '../components/CartCounter'; // Your provided counter
import { CustomerSelect } from '../components/CustomerSelect';
import { ContactPersonSelect } from '../components/ContactPersonSelect';

// Date handling
import { addDays, format } from "date-fns";
import { compareOsloDates, osloDateTimeToUtcIso, osloDateToPickerDate, toOsloDateString } from '@/lib/osloDates';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from '../config/firebase';
import { useNavigate } from "react-router-dom";

// --- TODO: (Checklist Item 10) Replace with react-router-dom useNavigate if needed ---
const navigate = (path) => { console.warn(`Navigation to ${path} not implemented`); };

// Add a constant for the localStorage key
const CHECKOUT_STORAGE_KEY = 'dagslysCheckoutData';

// Minimal serializers to avoid bloated/invalid localStorage state
function serializeCustomer(customer) {
  if (!customer) return null;
  const remark =
    typeof customer.projectRemark === 'string'
      ? customer.projectRemark
      : '';
  return {
    id: customer.id ?? null,
    name: customer.name ?? customer.displayname ?? null,
    displayname: customer.displayname ?? customer.name ?? null,
    isCustom: !!customer.isCustom,
    projectRemark: remark,
  };
}

function serializeContactPerson(person) {
  if (!person) return null;
  return {
    id: person.id ?? null,
    name: person.name ?? person.displayname ?? null,
    displayname: person.displayname ?? person.name ?? null,
    isCustom: !!person.isCustom,
    firstname: person.firstName ?? person.firstname ?? null,
    lastname: person.lastName ?? person.lastname ?? null,
    email: person.email ?? null,
    phone: person.phone ?? null,
    mobilephone: person.mobilephone ?? person.mobile ?? null,
    telephone: person.telephone ?? null,
  };
}

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// Helper to get initial checkout state from localStorage (sanitized)
function getInitialCheckoutState() {
  const stored = safeParse(localStorage.getItem(CHECKOUT_STORAGE_KEY));
  if (!stored || typeof stored !== 'object') return {};
  const topLevelRemark =
    typeof stored.projectRemark === 'string' ? stored.projectRemark : '';
  let selectedCustomer = serializeCustomer(stored.selectedCustomer) || null;
  if (selectedCustomer && topLevelRemark && !selectedCustomer.projectRemark) {
    selectedCustomer = { ...selectedCustomer, projectRemark: topLevelRemark };
  }
  return {
    selectedCustomer,
    selectedContactPerson: serializeContactPerson(stored.selectedContactPerson) || null,
    invoiceReference: typeof stored.invoiceReference === 'string' ? stored.invoiceReference : '',
    projectRemark: topLevelRemark,
    bookingName: typeof stored.bookingName === 'string' ? stored.bookingName : '',
    formData: typeof stored.formData === 'object' && stored.formData !== null ? stored.formData : undefined,
  };
}

// ========================================================
// Checkout Component using shadcn/ui
// ========================================================
const Checkout = () => {
  const {
    cartItems,
    getCartTotalPriceWithFactor,
    calculatePriceWithFactor,
    getTaxAmount,
    getProtectionAmount,
    getGrandTotal,
    daysOfRent,
    rentalFactor,
    removeItemCompletely,
    checkoutDate,
    setCheckoutDate,
    checkinDate,
    setCheckinDate,
    shootStartDate,
    setShootStartDate,
    shootEndDate,
    setShootEndDate,
    setCart,
    clearCart
  } = useCart();
  const { currentUser, currentUserProfile } = useAuth();
  const { rentmanData } = useApp();
  const { customers, loading: customersLoading } = useRentmanCustomers();
  // Hydrate from localStorage on each mount (lazy initializers)
  const [selectedCustomer, setSelectedCustomer] = useState(() => {
    const stored = getInitialCheckoutState();
    return stored.selectedCustomer || null;
  });
  const [selectedContactPerson, setSelectedContactPerson] = useState(() => {
    const stored = getInitialCheckoutState();
    return stored.selectedContactPerson || null;
  });
  const [invoiceReference, setInvoiceReference] = useState(() => {
    const stored = getInitialCheckoutState();
    return stored.invoiceReference || '';
  });
  const [projectRemark, setProjectRemark] = useState(() => {
    const stored = getInitialCheckoutState();
    return stored.projectRemark || '';
  });
  const [bookingName, setBookingName] = useState(() => {
    const stored = getInitialCheckoutState();
    return stored.bookingName || '';
  });
  const [formData, setFormData] = useState(() => {
    const stored = getInitialCheckoutState();
    if (stored.formData) return stored.formData;
    return {
      firstName: currentUserProfile?.firstName || '',
      lastName: currentUserProfile?.lastName || '',
      email: currentUserProfile?.email || currentUser?.email || '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      country: '',
      notes: ''
    };
  });
  const [user, setUser] = useState({ email: "test@example.com" }); // Placeholder
  const [userData, setUserData] = useState({ firstName: "Test", lastName: "User", discount: 0 }); // Placeholder
  const [editItem, setEditItem] = useState(null); // Item being edited/deleted
  const [itemRemark, setItemRemark] = useState(""); // Temp remark for edit dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Display-only. Does not affect submit payload / sortedCartItemsWithOrder.
  const [sortBy, setSortBy] = useState('none');
  const [groupBy, setGroupBy] = useState('none');

  const isApproved = currentUserProfile?.approvalStatus === 'approved';
  const totalPrice = getCartTotalPriceWithFactor();
  const getDefaultGroupName = useCallback((item) => {
    const raw = item?.defaultgroup ?? item?.defaultGroup ?? item?.default_group ?? null;
    if (typeof raw === "string" && raw.trim().length > 0) return raw.trim();
    if (raw && typeof raw === "object") {
      if (typeof raw.name === "string" && raw.name.trim().length > 0) return raw.name.trim();
      if (typeof raw.displayname === "string" && raw.displayname.trim().length > 0) return raw.displayname.trim();
      if (typeof raw.displayName === "string" && raw.displayName.trim().length > 0) return raw.displayName.trim();
    }
    return "Uncategorized";
  }, []);

  // SUBMIT PAYLOAD ORDER — do not change without explicit user confirmation.
  // User-facing Sort/Group on this page is display-only and must never feed this list.
  // Deterministic checkout order:
  // 1) defaultGroup name order A -> Z
  // 2) item order by calculated price high->low within each defaultGroup
  const sortedCartItemsWithOrder = useMemo(() => {
    const compareItemsByPriceDesc = (a, b) => {
      const aUnitPrice = Number(a.price) || 0;
      const bUnitPrice = Number(b.price) || 0;
      if (bUnitPrice !== aUnitPrice) return bUnitPrice - aUnitPrice;
      return String(a.name || "").localeCompare(String(b.name || ""));
    };

    const groups = new Map();
    cartItems.forEach((item) => {
      const defaultGroupName = getDefaultGroupName(item);
      if (!groups.has(defaultGroupName)) groups.set(defaultGroupName, []);
      groups.get(defaultGroupName).push(item);
    });

    const sortedDefaultGroupNames = Array.from(groups.keys()).sort((a, b) => {
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
    });

    const flattened = sortedDefaultGroupNames.flatMap((defaultGroupName) =>
      [...(groups.get(defaultGroupName) || [])].sort(compareItemsByPriceDesc)
    );

    return flattened.map((item, index) => ({
      ...item,
      order: String(index + 1),
    }));
  }, [cartItems, getDefaultGroupName]);

  const getFolderOrderIndex = useCallback((folderName = "") => {
    const match = String(folderName).match(/^(\d+)\.\s*/);
    return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
  }, []);

  const { rootFolderByItemId } = useMemo(() => {
    const rootMap = new Map();

    const walk = (folders = [], rootFolderName = null) => {
      for (const folder of folders) {
        const nextRootName = rootFolderName || folder.name || "Uncategorized";
        for (const item of folder.items || []) {
          rootMap.set(String(item.id), nextRootName);
        }
        walk(folder.children || [], nextRootName);
      }
    };

    walk(rentmanData?.tree || []);
    return { rootFolderByItemId: rootMap };
  }, [rentmanData]);

  // Display-only Sort/Group for the equipment table. Not used by submit payload.
  const groupedDisplayItems = useMemo(() => {
    const compareItems = (a, b) => {
      const aUnitPrice = Number(a.price) || 0;
      const bUnitPrice = Number(b.price) || 0;

      switch (sortBy) {
        case 'price-asc':
          return aUnitPrice - bUnitPrice;
        case 'price-desc':
          return bUnitPrice - aUnitPrice;
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'none':
        default:
          return String(a.name || '').localeCompare(String(b.name || ''));
      }
    };

    if (groupBy === 'none') {
      const sortedItems = [...cartItems].sort(compareItems);
      return { 'All Items': sortedItems };
    }

    const resolveGroupName = (item) => {
      const key = String(item.id);
      if (groupBy === 'category') {
        return getDefaultGroupName(item);
      }
      return rootFolderByItemId.get(key) || 'Uncategorized';
    };

    const groups = new Map();
    cartItems.forEach((item) => {
      const folderName = resolveGroupName(item);
      if (!groups.has(folderName)) {
        groups.set(folderName, []);
      }
      groups.get(folderName).push(item);
    });

    const sortedGroupNames = Array.from(groups.keys()).sort((a, b) => {
      const orderDiff = getFolderOrderIndex(a) - getFolderOrderIndex(b);
      if (orderDiff !== 0) return orderDiff;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    });

    const orderedGroups = {};
    sortedGroupNames.forEach((groupName) => {
      orderedGroups[groupName] = [...(groups.get(groupName) || [])].sort(compareItems);
    });

    return orderedGroups;
  }, [
    cartItems,
    sortBy,
    groupBy,
    getDefaultGroupName,
    rootFolderByItemId,
    getFolderOrderIndex,
  ]);

  // Handle customer selection
  const handleCustomerSelect = useCallback((customer) => {
    setSelectedCustomer(prev => {
      // Clear contact person if customer changed or customer is null
      if (!customer) {
        setSelectedContactPerson(null);
        return null;
      }
      
      // Clear contact person if switching to/from different customer
      if (!prev || (prev.id && customer.id && prev.id !== customer.id) || 
          (!prev.id && customer.id) || (prev.id && !customer.id)) {
        console.log('[handleCustomerSelect] Clearing contact person because customer changed:', {
          prevCustomer: prev,
          newCustomer: customer
        });
        setSelectedContactPerson(null);
      }
      return customer;
    });
  }, []);

  // Handle contact person selection
  const handleContactPersonSelect = useCallback((contactPerson) => {
    if (contactPerson === null) {
      console.error('[handleContactPersonSelect] setSelectedContactPerson(null) called');
    }
    setSelectedContactPerson(contactPerson);
  }, []);

  // SUBMIT PAYLOAD: do not change this function or the data it returns without explicit user confirmation.
  // User-facing Sort/Group on the items table is display-only and is not used here.
  const buildResolvedBookingData = useCallback((clientRequestIdValue = "") => {
    const userDisplayName = currentUserProfile?.displayName || `${currentUserProfile?.firstName || ''} ${currentUserProfile?.lastName || ''}`.trim();
    const userEmail = currentUser?.email || '';
    const bookingRemark = selectedCustomer?.projectRemark || '';
    const fullProjectRemark = bookingRemark
      ? `${userDisplayName} - ${userEmail}.  --- ${bookingRemark}`
      : `${userDisplayName} - ${userEmail}.`;

    const resolvedCompanyName = (
      selectedCustomer?.name ||
      selectedCustomer?.displayname ||
      ""
    ).trim();
    const resolvedContactFirstName = (
      selectedContactPerson?.firstname ||
      selectedContactPerson?.firstName ||
      formData.firstName ||
      ""
    ).trim();
    const resolvedContactLastName = (
      selectedContactPerson?.lastname ||
      selectedContactPerson?.lastName ||
      formData.lastName ||
      ""
    ).trim();
    const resolvedContactEmail = (
      selectedContactPerson?.email ||
      formData.email ||
      ""
    ).trim();
    const resolvedContactPhone = (
      selectedContactPerson?.phone ||
      selectedContactPerson?.mobilephone ||
      selectedContactPerson?.mobile ||
      selectedContactPerson?.telephone ||
      formData.phone ||
      ""
    ).trim();
    const selectedContactPersonIdForLookup = (
      selectedContactPerson &&
      !selectedContactPerson.isCustom &&
      selectedContactPerson.id &&
      selectedContactPerson.id !== "custom"
    ) ? String(selectedContactPerson.id) : "";
    const productionCompanySource = selectedCustomer?.isCustom ? "Manual" : "Rentman";
    const contactPersonSource = (
      selectedCustomer?.isCustom || selectedContactPerson?.isCustom || !selectedContactPersonIdForLookup
    ) ? "Manual" : "Rentman";

    return {
      cartItems: sortedCartItemsWithOrder.map(item => {
        const resolvedDefaultGroupName = getDefaultGroupName(item);
        return ({
          id: item.id,
          name: item.name,
          defaultgroup: item.defaultgroup || resolvedDefaultGroupName,
          quantity: item.quantity,
          itemRemark: item.itemRemark || '',
          order: item.order,
        });
      }),
      userEmail: formData.email,
      userData: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        discount: currentUserProfile?.discount || 0,
      },
      production_company_name: resolvedCompanyName,
      production_company_source: productionCompanySource,
      contact_person_source: contactPersonSource,
      ...(selectedContactPersonIdForLookup ? {
        selected_contact_person_id: selectedContactPersonIdForLookup,
      } : {}),
      ...(selectedCustomer?.isCustom ? {
        contact_name: resolvedCompanyName,
        contact_person_first_name: resolvedContactFirstName,
        contact_person_lastname: resolvedContactLastName,
        contact_person_email: resolvedContactEmail,
        contact_person_phone: resolvedContactPhone,
      } : {
        linked_contact: selectedCustomer?.id ? `/contacts/${selectedCustomer.id}` : "",
        contact_person_first_name: resolvedContactFirstName,
        contact_person_lastname: resolvedContactLastName,
        contact_person_email: resolvedContactEmail,
        contact_person_phone: resolvedContactPhone,
      }),
      projectRemark: fullProjectRemark,
      bookingName: bookingName || `Booking from ${userDisplayName || userEmail}`,
      usageperiod_start: osloDateTimeToUtcIso(shootStartDate, "08:00"),
      usageperiod_end: osloDateTimeToUtcIso(shootEndDate, "23:59"),
      planperiod_start: osloDateTimeToUtcIso(checkoutDate, "08:00"),
      planperiod_end: osloDateTimeToUtcIso(checkinDate, "23:59"),
      external_reference: invoiceReference || '',
      clientRequestId: clientRequestIdValue,
    };
  }, [
    bookingName,
    checkinDate,
    checkoutDate,
    currentUser,
    currentUserProfile,
    formData,
    getDefaultGroupName,
    invoiceReference,
    selectedContactPerson,
    selectedCustomer,
    shootEndDate,
    shootStartDate,
    sortedCartItemsWithOrder,
  ]);

  useEffect(() => {
    console.log('[Checkout change] selectedCustomer changed:', selectedCustomer);
  }, [selectedCustomer]);

  useEffect(() => {
    console.log('[Checkout change] selectedContactPerson changed:', selectedContactPerson);
  }, [selectedContactPerson]);

  useEffect(() => {
    if (!selectedCustomer && !selectedContactPerson) return;
    const resolvedBookingDataPreview = buildResolvedBookingData("preview-on-selection-change");
    console.log('[Checkout change] resolved bookingData payload preview:', resolvedBookingDataPreview);
  }, [selectedCustomer, selectedContactPerson, buildResolvedBookingData]);

  // --- Persist minimal checkout data to localStorage on change ---
  useEffect(() => {
    const remarkForStorage =
      selectedCustomer?.projectRemark ?? projectRemark ?? '';
    const data = {
      selectedCustomer: serializeCustomer(selectedCustomer),
      selectedContactPerson: serializeContactPerson(selectedContactPerson),
      invoiceReference,
      projectRemark: remarkForStorage,
      bookingName,
      formData,
    };
    try {
      localStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  }, [selectedCustomer, selectedContactPerson, invoiceReference, projectRemark, bookingName, formData]);

  // --- Reconcile stored customer with latest API list ---
  useEffect(() => {
    if (customersLoading || !selectedCustomer || selectedCustomer.isCustom) return;
    if (!customers?.length) return;

    const match = customers.find(c => String(c.id) === String(selectedCustomer.id));
    if (match) {
      setSelectedCustomer(prev => {
        if (!prev || String(prev.id) !== String(match.id)) return prev;
        const preservedRemark =
          typeof prev.projectRemark === 'string' ? prev.projectRemark : '';
        return { ...match, projectRemark: preservedRemark };
      });
    } else {
      setSelectedCustomer(null);
      setSelectedContactPerson(null);
      toast.info('Previously selected customer is no longer available. Please select again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersLoading, customers]);

  // --- Reconcile stored contact person with API for the selected customer ---
  const { contactPersons, loading: contactsLoading } = useRentmanContactPersons(
    selectedCustomer && !selectedCustomer.isCustom ? selectedCustomer.id : null
  );

  useEffect(() => {
    if (contactsLoading || !selectedCustomer || selectedCustomer.isCustom) return;
    if (!selectedContactPerson || selectedContactPerson.isCustom) return;
    if (!contactPersons?.length) return;

    const match = contactPersons.find(p => String(p.id) === String(selectedContactPerson.id));
    if (match) {
      setSelectedContactPerson(prev => {
        if (!prev || String(prev.id) !== String(match.id)) return prev;
        return { ...match };
      });
    } else {
      setSelectedContactPerson(null);
      toast.info('Previously selected contact person is no longer available. Please select again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsLoading, contactPersons, selectedCustomer]);

  const validateDateOrder = useCallback((dates, showToast = true) => {
    const { checkout, shootStart, shootEnd, checkin } = dates;

    if (checkout && shootStart && compareOsloDates(shootStart, checkout) < 0) {
      if (showToast) {
        toast.error("Invalid Date Order", {
          description: "Shoot start cannot be before check out.",
        });
      }
      return false;
    }
    if (shootStart && shootEnd && compareOsloDates(shootEnd, shootStart) < 0) {
      if (showToast) {
        toast.error("Invalid Date Order", {
          description: "Shoot end cannot be before shoot start.",
        });
      }
      return false;
    }
    if (shootEnd && checkin && compareOsloDates(checkin, shootEnd) < 0) {
      if (showToast) {
        toast.error("Invalid Date Order", {
          description: "Check in cannot be before shoot end.",
        });
      }
      return false;
    }
    return true;
  }, []);

  const handleCheckoutChange = useCallback((date) => {
    setCheckoutDate(toOsloDateString(date));
    // Upstream change resets dependent dates.
    setShootStartDate(null);
    setShootEndDate(null);
    setCheckinDate(null);
  }, [setCheckoutDate, setShootStartDate, setShootEndDate, setCheckinDate]);

  const handleShootStartChange = useCallback((date) => {
    const next = toOsloDateString(date);
    if (next && checkoutDate && compareOsloDates(next, checkoutDate) < 0) {
      toast.error("Invalid Shoot Start", { description: "Shoot start cannot be before check out." });
      return;
    }
    setShootStartDate(next);
    setShootEndDate(null);
    setCheckinDate(null);
  }, [checkoutDate, setShootStartDate, setShootEndDate, setCheckinDate]);

  const handleShootEndChange = useCallback((date) => {
    const next = toOsloDateString(date);
    if (next && shootStartDate && compareOsloDates(next, shootStartDate) < 0) {
      toast.error("Invalid Shoot End", { description: "Shoot end cannot be before shoot start." });
      return;
    }
    setShootEndDate(next);
    setCheckinDate(null);
  }, [shootStartDate, setShootEndDate, setCheckinDate]);

  const handleCheckinChange = useCallback((date) => {
    const next = toOsloDateString(date);
    if (next && shootEndDate && compareOsloDates(next, shootEndDate) < 0) {
      toast.error("Invalid Check in", { description: "Check in cannot be before shoot end." });
      return;
    }
    setCheckinDate(next);
  }, [shootEndDate, setCheckinDate]);

  // --- Dialog Handlers ---
  const openEditDialog = (item) => {
    setEditItem(item);
    setItemRemark(item.itemRemark || "");
    setIsEditDialogOpen(true);
  };

  const handleSaveRemark = () => {
    if (!editItem) return;

    // Update the item's remark in the cart
    setCart(prevCart => {
      const newCart = { ...prevCart };
      if (newCart[editItem.id]) {
        newCart[editItem.id] = {
          ...newCart[editItem.id],
          details: {
            ...newCart[editItem.id].details,
            itemRemark: itemRemark
          }
        };
      }
      return newCart;
    });

    // Reset states after saving
    setItemRemark("");
    setIsEditDialogOpen(false);
    setEditItem(null);
    toast.success("Remark Saved", { description: `Remark updated for ${editItem.name}` });
  };

  const handleClearRemark = () => {
    if (!editItem) return;

    // Clear the item's remark in the cart
    setCart(prevCart => {
      const newCart = { ...prevCart };
      if (newCart[editItem.id]) {
        newCart[editItem.id] = {
          ...newCart[editItem.id],
          details: {
            ...newCart[editItem.id].details,
            itemRemark: ""
          }
        };
      }
      return newCart;
    });

    // Reset states after clearing
    setItemRemark("");
    setIsEditDialogOpen(false);
    setEditItem(null);
    toast.info("Remark Cleared", { description: `Remark cleared for ${editItem.name}` });
  };

  const openDeleteDialog = (item) => {
    setEditItem(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItemConfirm = () => {
    if (!editItem) return;
    removeItemCompletely(editItem.id);
    setIsDeleteDialogOpen(false);
    setEditItem(null);
    toast.success("Item Removed", { description: `${editItem.name} removed from cart.` });
  };

  const handleClearCart = () => {
    clearCart();
    toast.success("Cart Cleared", { description: "All items removed from cart." });
  };

  // (removed legacy createBooking helper – API is called directly in handleSubmit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic Validation
    if (cartItems.length === 0) {
      toast.error("Empty Cart", { description: "Please add items to your cart." });
      return;
    }
    if (!checkoutDate || !shootStartDate || !shootEndDate || !checkinDate) {
      toast.error("Invalid Dates", {
        description: "Please select check out, shoot start, shoot end, and check in dates.",
      });
      return;
    }
    if (!validateDateOrder({
      checkout: checkoutDate,
      shootStart: shootStartDate,
      shootEnd: shootEndDate,
      checkin: checkinDate,
    })) {
      return;
    }
    if (!selectedCustomer) {
      toast.error("Missing Customer", { description: "Please select a customer." });
      return;
    }
    if (!selectedContactPerson && !selectedCustomer.isCustom) {
      toast.error("Missing Contact Person", { description: "Please select a contact person." });
      return;
    }

    setIsLoading(true);

    try {
      const clientRequestId = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const bookingData = buildResolvedBookingData(clientRequestId);

      // Explicit checkout submit logs for verification/debugging.
      console.log('[Checkout submit] selectedCustomer:', selectedCustomer);
      console.log('[Checkout submit] selectedContactPerson:', selectedContactPerson);
      console.log('[Checkout submit] bookingData payload:', bookingData);
      console.log('=== REQUEST BODY (bookingData) ===');
      console.log(JSON.stringify(bookingData, null, 2));
      console.log('=== END REQUEST BODY ===');
      console.log('=== SORTED CART ITEMS SENT TO BACKEND (DRY RUN TARGET) ===');
      console.log('[Checkout sort mode] defaultgroup + unit price DESC');
      console.table(
        bookingData.cartItems.map((item) => ({
          order: item.order,
          defaultgroup: item.defaultgroup || getDefaultGroupName(item),
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          itemRemark: item.itemRemark || '',
        }))
      );
      console.log('=== END SORTED CART ITEMS ===');
      
      console.log('Booking data context:', {
        formData,
        selectedCustomer,
        selectedContactPerson,
        currentUserProfile,
        shootStartDate,
        shootEndDate,
        checkoutDate,
        checkinDate,
        invoiceReference,
        bookingName
      });

      // Get the base URL for the API (unified)
      const baseUrl = getFunctionsBaseUrl();

      // Dev dry run: inspect payload without sending to backend.
      if (import.meta.env.DEV) {
        toast.info('Checkout dry run in dev', {
          description: 'Payload logged to console. Skipping backend request.',
        });
        return;
      }

      // Get a fresh ID token
      const idToken = await currentUser.getIdToken(true);

      // Call the createBooking endpoint
      let result;
      let projectRequestId = null;
      let rentmanProjectRequest = null;
      
      try {
        const response = await fetch(`${baseUrl}/createBooking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify(bookingData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create booking');
        }

        result = await response.json();

        if (!result.success) {
          throw new Error(result.message || 'Failed to create booking');
        }

        projectRequestId = result.projectRequestId;
        rentmanProjectRequest = result.projectRequest || null;
        console.log('[RENTMAN RETURN OBHJECT]', result);
      } catch (apiError) {
        // API call failed - booking was not created
        console.error('Error creating booking:', apiError);
        throw new Error(`Booking failed: ${apiError.message}`);
      }

      // API call succeeded - booking was created in Rentman
      // Now try to save to Firestore (this is non-critical)
      let firestoreWriteSucceeded = false;
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          console.warn('User document not found - booking succeeded but order not saved');
        } else {
          const userData = userDoc.data();
          const newOrder = {
            id: selectedCustomer?.projectRemark || projectRequestId || '',
            items: cartItems.map(item => ({
              id: item.id || '',
              name: item.name || '',
              rentmanId: item.rentmanId || '',
              quantity: parseInt(item.quantity) || 1,
              price: item.price || 0,
              itemRemark: item.itemRemark || '',
            })),
            customer: selectedCustomer?.isCustom ? {
              name: selectedCustomer?.name || '',
              email: formData?.email || '',
            } : {
              id: selectedCustomer?.id || '',
              name: selectedCustomer?.name || selectedCustomer?.displayname || '',
            },
            projectRemark: selectedCustomer?.projectRemark || '',
            usagePeriod: {
              from: shootStartDate || null,
              to: shootEndDate || null,
            },
            rentalDates: {
              from: checkoutDate || null,
              to: checkinDate || null,
            },
            totalPrice: totalPrice || 0,
            daysOfRent: daysOfRent || 0,
            createdAt: new Date().toISOString(),
            projectRequestId: projectRequestId || '',
            rentmanProjectRequest: rentmanProjectRequest || null,
            invoiceReference: invoiceReference || selectedCustomer?.projectRemark || '',
          };

          // Sanitize to avoid Firestore undefined errors (convert undefined to null)
          const sanitize = (o) => JSON.parse(JSON.stringify(o, (k, v) => v === undefined ? null : v));
          const updatedOrders = [...(userData.orders || []), sanitize(newOrder)];

          await updateDoc(userDocRef, { orders: updatedOrders });
          firestoreWriteSucceeded = true;
        }
      } catch (firestoreError) {
        // Firestore write failed but booking succeeded
        console.error('Error saving order to Firestore:', firestoreError);
        // Don't throw - booking succeeded, this is just a local save issue
      }

      // Clear the cart after successful booking (regardless of Firestore write)
      removeItemCompletely(null);
      localStorage.removeItem(CHECKOUT_STORAGE_KEY);

      // Show appropriate message based on Firestore write status
      if (firestoreWriteSucceeded) {
        toast.success('Order submitted successfully');
      } else {
        toast.success('Booking submitted successfully', {
          description: 'Your booking was created, but there was an issue saving it to your order history. Please contact support if you need the booking details.',
        });
      }

      navigate('/orders');
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error('Failed to submit order', { description: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    setCart(prevCart => {
      const newCart = { ...prevCart };
      if (newCart[itemId]) {
        newCart[itemId] = {
          ...newCart[itemId],
          quantity: newQuantity
        };
      }
      return newCart;
    });
  };

  // Add a clearBookingDetails handler
  const clearBookingDetails = useCallback(() => {
    setSelectedCustomer(null);
    setSelectedContactPerson(null);
    setInvoiceReference('');
    setProjectRemark('');
    setBookingName('');
    setFormData({
      firstName: currentUserProfile?.firstName || '',
      lastName: currentUserProfile?.lastName || '',
      email: currentUserProfile?.email || currentUser?.email || '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      country: '',
      notes: ''
    });
    try {
      localStorage.removeItem(CHECKOUT_STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing booking details from localStorage:', e);
    }
  }, [currentUserProfile, currentUser]);

  // Render
  // console.log('Current usagePeriod:', usagePeriod);
  return (
    <div className="container mx-auto p-4 md:p-6 lg:p-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold">Check out</h1>
        {cartItems.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Trash2 className="mr-2 h-4 w-4" /> Clear Cart
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear Entire Cart?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. All items will be removed from your cart.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleClearCart} className={cn(buttonVariants({ variant: "destructive" }))}>
                  Yes, Clear Cart
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <Separator className="my-6" />

      {/* Cart Items Section — Sort/Group is display-only; submit still uses sortedCartItemsWithOrder */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
        <h2 className="text-2xl font-semibold">Your Items</h2>
        {cartItems.length > 0 && (
          <div className="flex flex-row items-center gap-2">
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">Sort</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="w-[90px] px-2 text-xs justify-between">
                    {(() => {
                      switch (sortBy) {
                        case 'price-asc': return 'Low';
                        case 'price-desc': return 'High';
                        case 'name-asc': return 'A-Z';
                        case 'name-desc': return 'Z-A';
                        default: return 'None';
                      }
                    })()}
                    <ChevronDown className="h-3 w-3 opacity-50 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => setSortBy('none')}>
                    <span className="md:hidden">None</span>
                    <span className="hidden md:inline">No Sorting</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy('price-asc')}>
                    <span className="md:hidden">Low</span>
                    <span className="hidden md:inline">Price: Low to High</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy('price-desc')}>
                    <span className="md:hidden">High</span>
                    <span className="hidden md:inline">Price: High to Low</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy('name-asc')}>
                    <span className="md:hidden">A-Z</span>
                    <span className="hidden md:inline">Name: A to Z</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy('name-desc')}>
                    <span className="md:hidden">Z-A</span>
                    <span className="hidden md:inline">Name: Z to A</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">Group</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="w-[90px] px-2 text-xs justify-between">
                    {(() => {
                      switch (groupBy) {
                        case 'folder': return 'Group';
                        case 'category': return 'Category';
                        default: return 'None';
                      }
                    })()}
                    <ChevronDown className="h-3 w-3 opacity-50 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => setGroupBy('none')}>
                    <span className="md:hidden">None</span>
                    <span className="hidden md:inline">No Grouping</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setGroupBy('folder')}>
                    <span className="md:hidden">Group</span>
                    <span className="hidden md:inline">Equipment Group</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setGroupBy('category')}>
                    <span className="md:hidden">Category</span>
                    <span className="hidden md:inline">Equipment Category</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
      </div>
      <div className="border rounded-lg overflow-hidden mb-8">
        <Table className="checkout-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[15%]">Quantity</TableHead>
              <TableHead className="w-[20%]">Item</TableHead>
              <TableHead className="w-[40%]">Item Remark</TableHead>
              <TableHead className="w-[15%] text-right">Unit Price / day</TableHead>
              <TableHead className="w-[10%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cartItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  Your cart is empty.
                </TableCell>
              </TableRow>
            ) : (
              Object.entries(groupedDisplayItems).map(([folderName, items]) => (
                <React.Fragment key={folderName}>
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={5} className="bg-muted/30 py-2">
                      <div className="flex items-center">
                        <span className="text-sm font-semibold">{folderName}</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {items.length} {items.length === 1 ? 'item' : 'items'}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <CartCounter item={item} />
                      </TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.itemRemark}</TableCell>
                      <TableCell className="text-right">{calculatePriceWithFactor(item.price, 1)} NOK</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(item)}>
                              Edit Remark
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openDeleteDialog(item)}>
                              Remove Item
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Order Summary */}
      <h2 className="text-2xl font-semibold mb-3">Order Summary</h2>
      <div className="border rounded-lg p-6">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{getCartTotalPriceWithFactor()} NOK</span>
          </div>
          <div className="flex justify-between">
            <span>Rental Factor ({daysOfRent} days)</span>
            <span>x{rentalFactor}</span>
          </div>
          <div className="flex justify-between">
            <span>Tax (25%)</span>
            <span>{getTaxAmount()} NOK</span>
          </div>
          <div className="flex justify-between">
            <span>Insurance (5%)</span>
            <span>{getProtectionAmount()} NOK</span>
          </div>
          <Separator className="my-4" />
          <div className="flex justify-between font-semibold">
            <span>Estimated Total</span>
            <span>{getGrandTotal()} NOK</span>
          </div>
        </div>
      </div>

      {/* Booking Details Section */}
      <h2 className="text-2xl font-semibold mb-3 mt-12 flex items-center justify-between">
        <span>Booking Details</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearBookingDetails}
          className="ml-2"
        >
          Clear Booking Details
        </Button>
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6 border p-6 rounded-md mb-8">
        {/* Customer Selection */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Customer</label>
          <CustomerSelect
            onSelect={handleCustomerSelect}
            selectedCustomer={selectedCustomer}
          />
        </div>

        {/* Contact Person Selection */}
        {selectedCustomer && (
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Contact Person {!selectedCustomer.isCustom && `- ${selectedCustomer.displayname}`}
            </label>
            <ContactPersonSelect
              onSelect={handleContactPersonSelect}
              selectedContactPerson={selectedContactPerson}
              selectedCustomerId={selectedCustomer.isCustom ? null : selectedCustomer.id}
              isCustomerCustom={selectedCustomer.isCustom}
            />
          </div>
        )}

        {/* Booking Name */}
        <div className="space-y-2">
          <Label htmlFor="bookingName">Booking Name</Label>
          <Input
            id="bookingName"
            value={bookingName}
            onChange={(e) => setBookingName(e.target.value)}
            placeholder="e.g., Summer Production 2025"
            className="mt-1"
          />
          <p className="text-xs text-muted-foreground">Optional: A name to identify this booking request</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Date Pickers */}
          <div className="md:col-span-2 space-y-4">
            <div>
              <Label htmlFor="checkoutDate">Check out<span className="text-destructive">*</span></Label>
              <DatePicker
                value={osloDateToPickerDate(checkoutDate)}
                onSelect={handleCheckoutChange}
                className="mt-1 w-full"
                disabled={{
                  before: new Date(), // Disable past dates
                  after: addDays(new Date(), 365) // Disable dates more than 1 year in future
                }}
              />
            </div>
            <div>
              <Label htmlFor="shootStartDate">Shoot Start<span className="text-destructive">*</span></Label>
              <DatePicker
                value={osloDateToPickerDate(shootStartDate)}
                onSelect={handleShootStartChange}
                className="mt-1 w-full"
                disabled={{
                  before: osloDateToPickerDate(checkoutDate) || new Date(),
                  after: addDays(new Date(), 365),
                }}
              />
            </div>
            <div>
              <Label htmlFor="shootEndDate">Shoot End<span className="text-destructive">*</span></Label>
              <DatePicker
                value={osloDateToPickerDate(shootEndDate)}
                onSelect={handleShootEndChange}
                className="mt-1 w-full"
                disabled={{
                  before: osloDateToPickerDate(shootStartDate) || osloDateToPickerDate(checkoutDate) || new Date(),
                  after: addDays(new Date(), 365),
                }}
              />
            </div>
            <div>
              <Label htmlFor="checkinDate">Check in<span className="text-destructive">*</span></Label>
              <DatePicker
                value={osloDateToPickerDate(checkinDate)}
                onSelect={handleCheckinChange}
                className="mt-1 w-full"
                disabled={{
                  before: osloDateToPickerDate(shootEndDate) || osloDateToPickerDate(checkoutDate) || new Date(),
                  after: addDays(new Date(), 365)
                }}
              />
            </div>
            {daysOfRent > 0 && (
              <p className="text-sm text-muted-foreground">Total Days: {daysOfRent} {daysOfRent === 1 ? 'day' : 'days'}</p>
            )}
          </div>
          {/* Remarks */}
          <div className="md:col-span-2">
            <Label htmlFor="projectRemark">Booking Remarks</Label>
            <Textarea
              id="projectRemark"
              value={selectedCustomer?.projectRemark || ''}
              onChange={(e) => {
                setSelectedCustomer(prevUser => ({ ...prevUser, projectRemark: e.target.value }));
              }}
              className="mt-1 border rounded-md bg-background border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
              placeholder="Optional notes for the entire booking..."
              rows={3}
            />
          </div>
        </div>

        {/* Summary */}
        <Separator className="my-4" />
        <div className="space-y-1 text-right mb-4">
          <p className="text-lg font-semibold">
            Estimated Total: {getGrandTotal()} NOK
          </p>
          {/* TODO: (Checklist Item 7) Add discount display if using userData.discount */}
          {/* {userData?.discount > 0 && ( <p>...</p> )} */}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full"
          disabled={
            isLoading ||
            cartItems.length === 0 ||
            !checkoutDate ||
            !shootStartDate ||
            !shootEndDate ||
            !checkinDate ||
            !selectedCustomer ||
            (!selectedCustomer && !selectedCustomer.isCustom)
          }
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            "Submit Booking Request"
          )}
        </Button>
      </form>

      {/* --- Dialogs --- */}
      {/* Dialog for Editing Remark */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setItemRemark("");
          setEditItem(null);
        }
        setIsEditDialogOpen(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Remark for {editItem?.name}</DialogTitle>
            <DialogDescription>Add or modify the remark for this specific item.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="itemRemarkEdit" className="sr-only">Remark</Label>
            <Textarea
              id="itemRemarkEdit"
              autoFocus
              value={itemRemark}
              onChange={(e) => {
                if (e.target.value.length <= 100) {
                  setItemRemark(e.target.value);
                }
              }}
              placeholder="Add item specific notes... (max 200 characters)"
              className="min-h-[80px]"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {itemRemark.length}/100 characters
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button variant="secondary" onClick={handleClearRemark}>Clear Remark</Button>
            <Button onClick={handleSaveRemark}>Save Remark</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog for Deleting Item */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setEditItem(null);
        }
        setIsDeleteDialogOpen(open);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove all {editItem?.quantity} of {editItem?.name} from the cart?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItemConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* --- End Dialogs --- */}

    </div>
  );
};

export default Checkout;